import { UI_LANGUAGES } from "@/i18n/languages";
import { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  MoreHorizontal,
  ChevronsUpDown,
  Search,
  Flag,
  Trash2,
  Share,
  Bot,
  Package,
  LogIn,
  Globe,
  Sun,
  Moon,
  Check,
  Headset,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ModelThumbnailFallback } from "@/components/common/ModelThumbnailFallback";
import {
  useLLMDialog,
  useLLMSelection,
  useLLMDropdown,
} from "@/features/llm-selection/hooks";
import { cn, getModelThumbnailUrl } from "@/lib/utils";
import { useFloatingHeader } from "./use-floating-header";
import {
  OmuzLogo,
  HarmonySearchMd,
  HarmonyHamburger,
} from "@/components/icons/harmony-icons-inline";
import { HarmonyShoppingCart } from "@/components/icons";
import type { LLMModel } from "@/api/models/api-types";
import { useCurrentChatId } from "@/hooks";
import {
  useConversationMessagesInfinite,
  useDeleteConversation,
} from "@/api/hooks";
import { queryService } from "@/api/services/query-service";
import { useConversationsStore } from "@/features/conversations/store/conversations-store";
import { useToast } from "@/features/toast";
import { useModalStore } from "@/store/use-modal-store";
import { useShareChat } from "@/features/chat/share/hooks/use-share-chat";
import { useGlobalSearch } from "@/features/search/hooks/use-global-search";
import { HeaderCreditsIndicator } from "@/components/common/HeaderCreditsIndicator";
import { HeaderCartIndicator } from "@/components/common/HeaderCartIndicator";
import { TemporaryChatButton } from "@/features/temporary-chat";
import mixpanel from "mixpanel-browser";
import { useLeftSidebar } from "@/components/ui/sidebar";
import harmonaiLogo from "@/assets/harmonyai-logo.svg";
import { useMobileHeaderContext } from "./use-mobile-header-context";
import { useCart } from "@/features/cart";
import { useOrdersStore } from "@/features/orders/store/orders-store";
import useAuthStore from "@/store/use-auth-store";
import { persistAuthGateRedirect } from "@/features/auth-gate";
import {
  useTheme,
  useLanguage,
} from "@/store/use-ui-preferences-store";
import { isModifiedClick } from "@/lib/link-utils";
import { headerActionClass, headerToolbarClass } from "./header-action";

// 🔓 Guest header'ındaki dil seçici için (oturumlu kullanıcı ayarlardan yönetiyor)
const GUEST_LANGUAGES = UI_LANGUAGES;

export const GlobalHeader: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const floating = useFloatingHeader();

  // Bağlam kararı MobileBreadcrumbBar ile paylaşılır (tek kaynak)
  const {
    mobileContext,
    isChatPage,
    isOrdersPage,
    isCartPage,
    isCheckoutPage,
  } = useMobileHeaderContext();

  const { open: isSidebarOpen, setOpen: setSidebarOpen } = useLeftSidebar();

  // 🔓 Public mod: guest kullanıcıya login/register CTA'ları gösterilir,
  // auth gerektiren header aksiyonları (credits, cart, omuz vb.) gizlenir.
  const { isAuthenticated } = useAuthStore();
  const isGuest = !isAuthenticated();

  // 🔓 Guest tema/dil kontrolleri (localStorage'a persist edilir,
  // kayıt sonrası tercih aynen devam eder)
  const { isDark, toggleTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const currentGuestLanguage =
    GUEST_LANGUAGES.find((lang) => lang.code === language) ??
    GUEST_LANGUAGES[1];

  const handleGuestAuthNavigate = () => {
    // Login/register sonrası kullanıcı kaldığı sayfaya dönsün
    persistAuthGateRedirect(location.pathname + location.search);
  };

  // ─── Commerce actions (mobile) ────────────────────────────────────────────
  const { totalItems: cartTotalItems, isLoading: isCartLoading } = useCart();
  const { setActiveTab } = useOrdersStore();

  const handleMobileOrdersClick = (e: React.MouseEvent) => {
    if (isModifiedClick(e)) return; // yeni sekmede açılıyor, mevcut sekmenin aktif tab'ı değişmesin
    setActiveTab("orders");
  };

  // ─── Model Selector ──────────────────────────────────────────────────────────
  const { openModal } = useLLMDialog();
  const { selectedModel, selectModel } = useLLMSelection();
  // 🐛 Fix: masaüstü ve mobil tetikleyiciler aynı DropdownMenuContent'i paylaşıyordu.
  // Tek bir `isModelDropdownOpen` state'i her iki <DropdownMenu>'yü de kontrol edince,
  // biri tetiklendiğinde diğeri de (görünmese bile) portal üzerinden açılıp iki panel
  // birden ekranda görünüyordu. Her breakpoint'in kendi open state'i olmalı.
  const [isDesktopModelDropdownOpen, setIsDesktopModelDropdownOpen] =
    useState(false);
  const [isMobileModelDropdownOpen, setIsMobileModelDropdownOpen] =
    useState(false);
  const isModelDropdownOpen =
    isDesktopModelDropdownOpen || isMobileModelDropdownOpen;
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    searchQuery: modelSearchQuery,
    visibleModels,
    isLoading: isLoadingModels,
    isLoadingMore,
    updateSearchQuery,
    handleScroll: hookHandleScroll,
    resetVisibleItems,
  } = useLLMDropdown({
    enabled: isModelDropdownOpen,
  });

  useEffect(() => {
    if (!isModelDropdownOpen) {
      updateSearchQuery("");
      resetVisibleItems();
    }
  }, [isModelDropdownOpen, updateSearchQuery, resetVisibleItems]);

  const selectedModelThumbnailUrl = selectedModel
    ? getModelThumbnailUrl(selectedModel)
    : null;

  const handleModelScroll = (_e: React.UIEvent<HTMLDivElement>) => {
    if (scrollRef.current) {
      hookHandleScroll(scrollRef.current);
    }
  };

  const handleModelSelect = (model: LLMModel, index: number) => {
    selectModel(model);
    setIsDesktopModelDropdownOpen(false);
    setIsMobileModelDropdownOpen(false);
    mixpanel.track("model_selected", {
      model_id: model?._id,
      model_name: model?.name,
      position: index.toString(),
      type: "global header",
    });
    window.netmera?.push(function (api: any) {
      api.sendEvent({
        code: "lvpxz",
        ea: model?._id,
        eb: model?.name,
        ef: "global header",
        ee: index.toString(),
      });
    });
  };

  // ─── Chat Actions (only on /chat/:id) ────────────────────────────────────────
  const currentChatId = useCurrentChatId();
  const { deleteConversation } = useDeleteConversation();
  const { setDeleting } = useConversationsStore();
  const { success, error: showError } = useToast();
  const { openDeleteChat } = useModalStore();
  const { createShareLink } = useShareChat();

  const shareTokenFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("token");
  }, [location.search]);

  const { data: infiniteMessagesData } = useConversationMessagesInfinite(
    isChatPage ? (currentChatId || null) : null,
    20,
    shareTokenFromUrl,
  );

  const isPublicFromInfiniteScroll =
    infiniteMessagesData?.pages?.[0]?.data?.isPublic;
  const canEditFromInfiniteScroll =
    infiniteMessagesData?.pages?.[0]?.data?.canEdit;

  // "Shared" rozeti için isPublic mesaj yanıtından geliyor (ekstra istek yok)
  const isPublic = isPublicFromInfiniteScroll ?? false;

  const handleShareClick = async () => {
    if (!currentChatId) {
      showError(t("error.noModelSelected") || "No chat selected");
      return;
    }

    // Mevcut shareToken'ı LAZY olarak çek: backend'de conversation-detail
    // endpoint'i olmadığı için liste üzerinden bulunuyor. Bu istek eskiden
    // her chat sayfası açılışında atılıyordu; artık yalnızca share
    // tıklanınca çalışır. Token bulunamazsa createShareLink yeni link üretir.
    let shareTokenFromConversation: string | null | undefined;
    let conversationIsPublic: boolean | undefined;
    try {
      const response = await queryService.getConversations({
        page: 1,
        limit: 50,
        sortBy: "-createdAt",
      });
      const currentConversation = response.data?.result?.find(
        (conv) => conv._id === currentChatId,
      );
      shareTokenFromConversation = currentConversation?.shareToken;
      conversationIsPublic = currentConversation?.isPublic;
    } catch (lookupError) {
      console.warn("Share token lookup failed:", lookupError);
    }

    createShareLink(
      currentChatId,
      isPublicFromInfiniteScroll ??
        conversationIsPublic ??
        Boolean(shareTokenFromConversation),
      shareTokenFromConversation,
    );
  };

  const handleReportClick = () => {
    // TODO: Implement report functionality
  };

  const handleDeleteChatClick = () => {
    if (!currentChatId) {
      showError(t("chat.deleteError"), { title: t("error.title") });
      return;
    }
    if (canEditFromInfiniteScroll === false) {
      showError(t("chat.share.disableComingSoon"), { title: t("error.title") });
      return;
    }

    openDeleteChat(async () => {
      setDeleting(currentChatId, true);
      try {
        navigate("/", { replace: true });
        await deleteConversation(currentChatId);
        success(t("chat.deleteSuccess"), { title: t("common.success") });
      } catch (deleteError: any) {
        const errorMessage = deleteError?.message || t("chat.deleteError");
        showError(errorMessage, {
          title: t("error.title"),
          actions: [{ label: t("common.retry"), action: () => handleDeleteChatClick() }],
        });
      } finally {
        setDeleting(currentChatId, false);
      }
    });
  };

  const handleOmuzClick = () => {
    // TODO: Implement Omuz profile navigation
  };

  // ─── Search ───────────────────────────────────────────────────────────────────
  const {
    onOpenSearch,
    isSearchOpen,
    searchQuery: globalSearchQuery,
  } = useGlobalSearch();
  const [isSearchHovered, setIsSearchHovered] = useState(false);

  const isMac =
    typeof navigator !== "undefined" &&
    navigator.platform.toLowerCase().includes("mac");
  const isSearchExpanded =
    isSearchHovered || isSearchOpen || Boolean(globalSearchQuery.trim());

  // ─── Shared model dropdown content (reused on mobile & desktop) ─────────────
  const modelDropdownContent = (
    <DropdownMenuContent
      align="start"
      className="w-64 bg-bg-primary dark:bg-gray-800 border-border-secondary dark:border-gray-600 z-[999] rounded-2xl shadow-lg"
    >
      <div className="px-2 py-2 rounded-lg">
        <div className="relative">
          <Input
            placeholder={t("chat.searchModel")}
            value={modelSearchQuery}
            onChange={(e) => updateSearchQuery(e.target.value)}
            // 🐛 Fix: Radix DropdownMenu açıkken tuş vuruşlarını kendi typeahead/roving-focus
            // mantığı için Content düzeyinde dinliyor; stopPropagation olmadan her tuşta
            // input focus'unu kaybediyordu.
            onKeyDown={(e) => e.stopPropagation()}
            className="h-10 text-sm bg-transparent border border-border-secondary rounded-lg"
          />
          <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-text-quaternary dark:text-gray-400" />
        </div>
      </div>
      <div
        ref={scrollRef}
        className="max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
        onScroll={handleModelScroll}
      >
        {isLoadingModels ? (
          <div className="px-2 py-4 text-center text-sm text-text-quaternary dark:text-gray-400">
            {t("common.loading")}
          </div>
        ) : visibleModels.length === 0 ? (
          <div className="px-2 py-4 text-center text-sm text-text-quaternary dark:text-gray-400">
            {t("chat.noResults")}
          </div>
        ) : (
          visibleModels.map((model, index: number) => {
            const modelThumbnailUrl = getModelThumbnailUrl(model);
            return (
              <DropdownMenuItem
                key={model._id}
                onClick={() => handleModelSelect(model, index)}
                className={`flex items-center gap-2 cursor-pointer data-[highlighted]:bg-bg-secondary data-[highlighted]:text-text-primary py-3 rounded-lg ${
                  selectedModel?._id === model._id ? "text-module-accent" : ""
                }`}
              >
                <div className="w-6 h-6 rounded overflow-hidden flex-shrink-0">
                  {modelThumbnailUrl ? (
                    <img
                      src={modelThumbnailUrl}
                      alt={model.title || model.name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <ModelThumbnailFallback
                      title={model.title || model.name}
                      size="sm"
                      className="w-full h-full"
                    />
                  )}
                </div>
                <span className="text-sm">{model.title || model.name}</span>
              </DropdownMenuItem>
            );
          })
        )}
        {isLoadingMore && (
          <div className="px-2 py-2 text-center text-sm text-text-quaternary dark:text-gray-400">
            {t("common.loading")}...
          </div>
        )}
      </div>
      <DropdownMenuItem
        onClick={() => openModal()}
        className="flex items-center gap-2 cursor-pointer text-module-accent data-[highlighted]:bg-bg-secondary data-[highlighted]:text-module-accent py-3"
      >
        <span>{t("chat.addMoreFavoriteModels")}</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  );

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <header
      className={cn(
        "flex z-[950] h-14 w-full shrink-0 items-center justify-between border-b px-3 md:px-4",
        "transition-[background-color,border-color,backdrop-filter] duration-300",
        floating
          ? // Anasayfa: içeriğin üstünde yüzer, tepede saydam; kaydırılınca cam zemin
            "absolute inset-x-0 top-0 border-transparent bg-transparent [[data-scrolled]_&]:border-border-secondary/70 [[data-scrolled]_&]:bg-bg-primary/95 [[data-scrolled]_&]:backdrop-blur-md dark:[[data-scrolled]_&]:border-gray-800 dark:[[data-scrolled]_&]:bg-gray-900/95"
          : "sticky top-0 border-border-secondary/70 bg-bg-primary/95 backdrop-blur-md dark:border-gray-800 dark:bg-gray-900/95",
      )}
    >
      {/* ══════════════════════════════════════════════════════════════════════
          DESKTOP LAYOUT (md+)
      ══════════════════════════════════════════════════════════════════════ */}

      {/* ── DESKTOP LEFT: Model Selector ─────────────────────────────────────── */}
      <div className="hidden md:flex items-center gap-3 min-w-0 shrink-0">
        <Link
          to="/"
          className={cn(
            "flex items-center justify-center rounded-lg h-8 w-8 shrink-0 transition-opacity hover:opacity-80",
            // 🔓 Guest'te sidebar (ve logosu) gizli olduğu için logo header'da gösterilir
            isGuest ? "md:flex" : "md:hidden",
          )}
          aria-label="HarmonyAI Home"
        >
          <img src={harmonaiLogo} alt="HarmonyAI" className="h-8 w-8" />
        </Link>

        {/* Desktop Model Selector */}
        {isChatPage && (
          <DropdownMenu
            open={isDesktopModelDropdownOpen}
            onOpenChange={setIsDesktopModelDropdownOpen}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-bg-primary dark:bg-gray-800 hover:bg-bg-primary-hover dark:hover:bg-gray-700 transition-colors border-border-secondary dark:border-gray-600"
              >
                {selectedModel && (
                  <div className="w-4 h-4 rounded overflow-hidden flex-shrink-0">
                    {selectedModelThumbnailUrl ? (
                      <img
                        src={selectedModelThumbnailUrl}
                        alt={selectedModel.title}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <ModelThumbnailFallback
                        title={selectedModel.title || selectedModel.name}
                        size="xs"
                        className="w-full h-full"
                      />
                    )}
                  </div>
                )}
                <span className="text-sm font-semibold text-text-primary dark:text-gray-100 truncate max-w-[120px] lg:max-w-[180px]">
                  {selectedModel?.title || t("chat.selectModel")}
                </span>
                <ChevronsUpDown className="w-4 h-4 text-text-quaternary dark:text-gray-400 flex-shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            {modelDropdownContent}
          </DropdownMenu>
        )}
      </div>

      {/* ── DESKTOP RIGHT: Actions ───────────────────────────────────────────── */}
      <div className="hidden md:flex items-center gap-1.5 shrink-0 min-w-0">
        {/* 🔓 Guest: Tema + Dil + Login / Register CTA'ları */}
        {isGuest && (
          <div className="flex items-center gap-2">
            {/* Destek — guest'te sidebar gizli olduğu için tek giriş noktası */}
            <Button
              asChild
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full text-text-secondary dark:text-gray-400 hover:text-text-primary dark:hover:text-gray-100 hover:bg-bg-secondary dark:hover:bg-gray-800"
              title={t("common.support")}
            >
              <Link to="/support" aria-label={t("common.support")}>
                <Headset className="size-4" />
              </Link>
            </Button>

            {/* Tema toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-8 w-8 rounded-full text-text-secondary dark:text-gray-400 hover:text-text-primary dark:hover:text-gray-100 hover:bg-bg-secondary dark:hover:bg-gray-800"
              title={t("common.theme")}
              aria-label={t("common.theme")}
            >
              {isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </Button>

            {/* Dil seçici */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2.5 rounded-full text-sm font-medium text-text-secondary dark:text-gray-400 hover:text-text-primary dark:hover:text-gray-100 hover:bg-bg-secondary dark:hover:bg-gray-800"
                  title={t("common.language")}
                  aria-label={t("common.language")}
                >
                  <Globe className="size-4 mr-1.5" />
                  {currentGuestLanguage.label}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-44 bg-bg-primary dark:bg-gray-800 border-border-secondary dark:border-gray-600 z-[999] rounded-2xl shadow-lg"
              >
                {GUEST_LANGUAGES.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className="flex items-center gap-2 cursor-pointer py-2 data-[highlighted]:bg-bg-secondary data-[highlighted]:text-text-primary"
                  >
                    <span className="text-base">{lang.flag}</span>
                    <span className="text-sm">{lang.fullLabel}</span>
                    {language === lang.code && (
                      <Check className="ml-auto size-4 text-module-accent" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              asChild
              variant="outline"
              size="sm"
              className="h-8 px-4 rounded-full font-semibold text-sm border-border-secondary dark:border-gray-600 dark:text-gray-100"
            >
              <Link to="/login" onClick={handleGuestAuthNavigate}>
                {t("authGate.login")}
              </Link>
            </Button>
            <Button
              asChild
              variant="darkPrimary"
              size="sm"
              className="h-8 px-4 rounded-full font-semibold text-sm"
            >
              <Link to="/register" onClick={handleGuestAuthNavigate}>
                {t("authGate.register")}
              </Link>
            </Button>
          </div>
        )}

        {!isGuest && (
        <div className={headerToolbarClass}>
        {/* Search */}
        <div
          className={cn(
            "h-8 shrink-0 w-8 transition-[width] duration-300 ease-out",
            isSearchExpanded ? "w-[280px]" : "w-8",
          )}
          onMouseEnter={() => setIsSearchHovered(true)}
          onMouseLeave={() => setIsSearchHovered(false)}
        >
          <button
            type="button"
            onClick={onOpenSearch}
            onFocus={() => setIsSearchHovered(true)}
            onBlur={() => setIsSearchHovered(false)}
            className={cn(headerActionClass, "w-full px-2")}
          >
            <div
              className={cn(
                "flex w-full items-center",
                isSearchExpanded ? "justify-start" : "justify-center",
              )}
            >
              <HarmonySearchMd size={15} className="shrink-0" />
              <span
                className={cn(
                  "overflow-hidden whitespace-nowrap text-sm text-left transition-all duration-300",
                  isSearchExpanded
                    ? "max-w-[180px] opacity-100 ml-2"
                    : "max-w-0 opacity-0 ml-0",
                )}
              >
                {t("common.search")}
              </span>
              <kbd
                className={cn(
                  "ml-auto items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium text-text-quaternary dark:text-gray-500 transition-all duration-300",
                  isSearchExpanded ? "hidden sm:inline-flex opacity-100" : "hidden",
                )}
              >
                {isMac ? "⌘⇧F" : "Ctrl+Shift+F"}
              </kbd>
            </div>
          </button>
        </div>

        {/* Credits */}
        <HeaderCreditsIndicator />

        {/* Cart */}
        <HeaderCartIndicator />

        <button
          type="button"
          onClick={handleOmuzClick}
          className={cn(headerActionClass, "gap-2 px-3")}
          title={t("chat.openOmuzProfile")}
        >
          <OmuzLogo className="size-4 text-[#FA6B17]" />
          <span className="hidden text-sm font-medium lg:inline">
            {t("chat.omuzProfile")}
          </span>
        </button>

        {/* Temporary Chat */}
        <TemporaryChatButton />

        {/* Chat More Options Dropdown */}
        {isChatPage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(headerActionClass, "size-8 p-0")}
                title={t("chat.moreOptions")}
              >
                <MoreHorizontal className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-40 bg-bg-primary dark:bg-gray-800 border-border-secondary dark:border-gray-600 z-[999] rounded-2xl shadow-lg"
            >
              <DropdownMenuItem
                onClick={handleShareClick}
                disabled={canEditFromInfiniteScroll === false}
                className="flex items-center gap-2 cursor-pointer py-2 data-[highlighted]:bg-bg-secondary data-[highlighted]:text-text-primary"
              >
                <Share className="w-4 h-4" />
                <span>{t("chat.shareChat")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleReportClick}
                className="flex items-center gap-2 cursor-pointer py-2 data-[highlighted]:bg-bg-secondary data-[highlighted]:text-text-primary"
              >
                <Flag className="w-4 h-4" />
                <span>{t("chat.report")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleDeleteChatClick}
                disabled={canEditFromInfiniteScroll === false}
                className="flex items-center gap-2 cursor-pointer text-red-600 dark:text-red-500 py-2 data-[highlighted]:bg-bg-secondary data-[highlighted]:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
                <span>{t("chat.deleteChat")}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        </div>
        )}

        {/* Paylaşım rozetleri ada dışında — renkli çip toolbar'ı parçalamasın */}
        {!isGuest && isChatPage && isPublic && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleShareClick}
              disabled={canEditFromInfiniteScroll === false}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold",
                "bg-module-accent/10 dark:bg-module-accent/15",
                "text-module-accent-strong",
                "border border-module-accent/30 dark:border-module-accent/25",
                canEditFromInfiniteScroll ? "cursor-pointer" : "cursor-default",
              )}
              aria-label={t("chat.shareView.sharedBadge")}
            >
              <Share className="h-3.5 w-3.5" />
              {t("chat.shareView.sharedBadge")}
            </button>
            {canEditFromInfiniteScroll === false && (
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold",
                  "bg-gray-100/70 dark:bg-gray-800/60",
                  "text-text-secondary dark:text-gray-300",
                  "border border-border-secondary/70 dark:border-gray-700/70",
                )}
                aria-label={t("chat.shareView.readOnlyBadge")}
              >
                {t("chat.shareView.readOnlyBadge")}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          MOBILE LAYOUT (< md) — ☰ | Logo | Icons
          home     → ☰ | Logo | Credits + Cart
          chat     → ☰ | Logo | Model + Search + ⋯
          commerce → ☰ | Logo | Orders/Cart toggle
          other    → ☰ | Logo | (boş; breadcrumb header altı şeritte)
      ══════════════════════════════════════════════════════════════════════ */}
      <div className="md:hidden relative flex w-full items-center min-h-9">

        {/* ── MOBILE LEFT: Hamburger ────────────────────────────────────────── */}
        <div className="relative z-10 flex items-center shrink-0">
          {!isGuest && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!isSidebarOpen)}
              className="h-9 w-9 rounded-xl text-foreground hover:bg-bg-secondary dark:hover:bg-gray-800 shrink-0"
              aria-label={t("common.toggleMenu")}
            >
              <HarmonyHamburger size={19} />
            </Button>
          )}
        </div>

        {/* ── MOBILE CENTER: Harmony logo ───────────────────────────────────── */}
        <Link
          to="/"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center rounded-lg h-8 w-8 shrink-0 transition-opacity hover:opacity-80"
          aria-label="HarmonyAI Home"
        >
          <img src={harmonaiLogo} alt="HarmonyAI" className="h-8 w-8" />
        </Link>

        {/* ── MOBILE RIGHT: Context icons ───────────────────────────────────── */}
        <div className="relative z-10 ml-auto flex items-center gap-1 shrink-0 min-w-0">

          {/* 🔓 Guest: her bağlamda Destek + Dil + Login CTA (sidebar/hamburger gizli) */}
          {isGuest && (
            <>
              <Button
                asChild
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-xl text-text-secondary dark:text-gray-400 hover:text-text-primary dark:hover:text-gray-100 hover:bg-bg-secondary dark:hover:bg-gray-800"
              >
                <Link to="/support" aria-label={t("common.support")}>
                  <Headset className="size-5" />
                </Link>
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-xl text-text-secondary dark:text-gray-400 hover:text-text-primary dark:hover:text-gray-100 hover:bg-bg-secondary dark:hover:bg-gray-800"
                    aria-label={t("common.language")}
                  >
                    <Globe className="size-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-44 bg-bg-primary dark:bg-gray-800 border-border-secondary dark:border-gray-600 z-[999] rounded-2xl shadow-lg"
                >
                  {GUEST_LANGUAGES.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className="flex items-center gap-2 cursor-pointer py-2.5 data-[highlighted]:bg-bg-secondary data-[highlighted]:text-text-primary"
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span className="text-sm">{lang.fullLabel}</span>
                      {language === lang.code && (
                        <Check className="ml-auto size-4 text-module-accent" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                asChild
                variant="darkPrimary"
                size="sm"
                className="h-8 px-3 rounded-full font-semibold text-xs"
              >
                <Link to="/login" onClick={handleGuestAuthNavigate}>
                  <LogIn className="size-3.5 mr-1.5" />
                  {t("authGate.login")}
                </Link>
              </Button>
            </>
          )}

          {/* HOME: Credits + Cart */}
          {mobileContext === "home" && !isGuest && (
            <div className={headerToolbarClass}>
              <HeaderCreditsIndicator />
              <HeaderCartIndicator />
            </div>
          )}

          {/* CHAT: Compact Model Selector + Search + More Options */}
          {mobileContext === "chat" && (
            <>
              <DropdownMenu
                open={isMobileModelDropdownOpen}
                onOpenChange={setIsMobileModelDropdownOpen}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-full bg-bg-secondary dark:bg-gray-800 hover:bg-bg-secondary-hover dark:hover:bg-gray-700 transition-colors border border-border-secondary/60 dark:border-gray-700"
                    aria-label={selectedModel?.title || t("chat.selectModel")}
                    title={selectedModel?.title || t("chat.selectModel")}
                  >
                    {selectedModelThumbnailUrl ? (
                      <div className="w-5 h-5 rounded overflow-hidden flex-shrink-0">
                        <img
                          src={selectedModelThumbnailUrl}
                          alt={selectedModel?.title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : selectedModel ? (
                      <ModelThumbnailFallback
                        title={selectedModel.title || selectedModel.name}
                        size="xs"
                        className="w-5 h-5 flex-shrink-0"
                      />
                    ) : (
                      <Bot className="w-5 h-5 text-text-quaternary dark:text-gray-400 flex-shrink-0" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                {modelDropdownContent}
              </DropdownMenu>

              <Button
                variant="ghost"
                size="icon"
                onClick={onOpenSearch}
                className="h-9 w-9 rounded-xl text-text-secondary dark:text-gray-400 hover:text-text-primary dark:hover:text-gray-100 hover:bg-bg-secondary dark:hover:bg-gray-800"
                aria-label={t("common.search")}
              >
                <HarmonySearchMd size={18} />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-xl text-text-secondary dark:text-gray-400 hover:text-text-primary dark:hover:text-gray-100 hover:bg-bg-secondary dark:hover:bg-gray-800"
                    aria-label={t("chat.moreOptions")}
                  >
                    <MoreHorizontal className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-44 bg-bg-primary dark:bg-gray-800 border-border-secondary dark:border-gray-600 z-[999] rounded-2xl shadow-lg"
                >
                  <DropdownMenuItem
                    onClick={handleShareClick}
                    disabled={canEditFromInfiniteScroll === false}
                    className="flex items-center gap-2 cursor-pointer py-2.5 data-[highlighted]:bg-bg-secondary data-[highlighted]:text-text-primary"
                  >
                    <Share className="w-4 h-4" />
                    <span>{t("chat.shareChat")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleReportClick}
                    className="flex items-center gap-2 cursor-pointer py-2.5 data-[highlighted]:bg-bg-secondary data-[highlighted]:text-text-primary"
                  >
                    <Flag className="w-4 h-4" />
                    <span>{t("chat.report")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleDeleteChatClick}
                    disabled={canEditFromInfiniteScroll === false}
                    className="flex items-center gap-2 cursor-pointer text-red-600 dark:text-red-500 py-2.5 data-[highlighted]:bg-bg-secondary data-[highlighted]:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>{t("chat.deleteChat")}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}

          {/* OTHER: sağ slot boş — breadcrumb header altındaki
              MobileBreadcrumbBar şeridine taşındı (MainLayout) */}

          {/* COMMERCE: Orders ↔ Cart toggle */}
          {mobileContext === "commerce" && (
            <>
              {(isCartPage || isCheckoutPage) && (
                <Button
                  asChild
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-9 w-9 rounded-xl text-text-secondary dark:text-gray-400 hover:text-text-primary dark:hover:text-gray-100 hover:bg-bg-secondary dark:hover:bg-gray-800",
                    isOrdersPage && "text-module-accent",
                  )}
                >
                  <Link to="/orders" onClick={handleMobileOrdersClick} aria-label={t("common.myOrders")}>
                    <Package className="h-5 w-5" />
                  </Link>
                </Button>
              )}
              {(isOrdersPage || isCheckoutPage) && (
                <div className="relative">
                  <Button
                    asChild
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-9 w-9 rounded-xl text-text-secondary dark:text-gray-400 hover:text-text-primary dark:hover:text-gray-100 hover:bg-bg-secondary dark:hover:bg-gray-800",
                      isCartPage && "text-module-accent",
                    )}
                  >
                    <Link to="/cart" aria-label={t("common.cart")}>
                      <HarmonyShoppingCart className="h-5 w-5" />
                      {isCartLoading ? (
                        <span className="absolute top-0.5 right-0.5 h-2 w-2 animate-pulse rounded-full bg-gray-300 dark:bg-gray-600" />
                      ) : (
                        cartTotalItems > 0 && (
                          <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-gray-900 text-[9px] font-bold text-white dark:bg-white dark:text-gray-900">
                            {cartTotalItems > 9 ? "9+" : cartTotalItems}
                          </span>
                        )
                      )}
                    </Link>
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </header>
  );
};
